import { zodResolver } from "@hookform/resolvers/zod";
import type { LinkedIn } from "@reactive-resume/parser";
import { dataImportTypeSchema, Pdf } from "@reactive-resume/utils";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@reactive-resume/ui";
import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { z } from "zod";
import { cn } from "@reactive-resume/utils";

import { useDialog } from "@/client/stores/dialog";

const importFormSchema = z.object({
  file: z.instanceof(File),
  type: dataImportTypeSchema,
});

export type ImportType = z.infer<typeof dataImportTypeSchema>;

type ImportFormValues = z.infer<typeof importFormSchema>;

enum ImportStep {
  IMPORT_RESUME = 0,
  JOB_DESCRIPTION = 1,
}

export type ValidationResult =
  | {
      isValid: false;
      errors: string;
    }
  | {
      isValid: true;
      type: ImportType;
      result: LinkedIn | Pdf;
    };

import { StepUploadOrSelect } from "./StepUploadOrSelect";
import { StepJobDescription } from "./StepJobDescription";
import { Upload } from "lucide-react";
import { t } from "@lingui/macro";
import { UserFocus } from "@phosphor-icons/react";
import type { ResumeDto } from "@reactive-resume/dto";

export const ImportDialog = () => {
  const { isOpen, close, payload } = useDialog("import");

  const [currentStep, setCurrentStep] = useState<ImportStep>(ImportStep.IMPORT_RESUME);
  const [validationResult, setValidationResult] = useState<ValidationResult | null>(null);
  const [isValidating, setIsValidating] = useState(false);
  const [uploadedFile, setUploadedFile] = useState<File | null>(null);
  const [uploadedType, setUploadedType] = useState<ImportType | null>(null);
  const [selectedResume, setSelectedResume] = useState<ResumeDto | null>(null);

  const importForm = useForm<ImportFormValues>({
    defaultValues: {
      type: dataImportTypeSchema.enum["pdf-resume"],
    },
    resolver: zodResolver(importFormSchema),
  });

  const filetype = importForm.watch("type");

  useEffect(() => {
    importForm.reset({ file: undefined, type: filetype });
    setValidationResult(null);
    setUploadedFile(null);
    setUploadedType(null);
  }, [filetype]);

  // Reset all states when dialog is closed
  useEffect(() => {
    if (!isOpen) {
      setCurrentStep(ImportStep.IMPORT_RESUME);
      setValidationResult(null);
      setIsValidating(false);
      setUploadedFile(null);
      setUploadedType(null);
      setSelectedResume(null);
      importForm.reset({
        type: dataImportTypeSchema.enum["pdf-resume"],
      });
    }
  }, [isOpen, importForm]);

  // Auto-proceed to job description step when file is uploaded
  const handleFileUploaded = (file: File, type: ImportType) => {
    setUploadedFile(file);
    setUploadedType(type);
    setSelectedResume(null); // Clear selected resume when uploading file
    setCurrentStep(ImportStep.JOB_DESCRIPTION);
  };

  // Handle resume selection
  const handleResumeSelected = (resume: ResumeDto) => {
    setSelectedResume(resume);
    setUploadedFile(null); // Clear uploaded file when selecting resume
    setUploadedType(null);
    setCurrentStep(ImportStep.JOB_DESCRIPTION);
  };

  return (
    <Dialog open={isOpen} onOpenChange={close}>
      <DialogContent
        className={`max-h-[95vh] max-w-5xl overflow-y-auto lg:max-w-4xl ${payload?.lightMode ? "light" : ""}`}
      >
        <DialogHeader className="border-b border-gray-200 pb-4">
          <DialogTitle>
            <div className="flex items-center gap-2 text-xl">
              <UserFocus size={32} className="text-primary" />
              {t`Create a new resume`}
            </div>
          </DialogTitle>
          <DialogDescription className="-mt-2 text-sm">
            {t`Upload a resume file or select from your existing resumes, then add a job description to create a tailored resume.`}
          </DialogDescription>
        </DialogHeader>
        <div className="my-6 flex items-center justify-center space-x-4">
          <div className={cn("text-primary [&>div]:bg-primary flex items-center space-x-2")}>
            <div className="flex h-8 w-8 items-center justify-center rounded-full font-semibold text-white">
              1
            </div>
            <span className="font-semibold">{t`Select Resume`}</span>
          </div>
          <div
            className={cn("h-1 w-32", {
              "bg-primary": currentStep === ImportStep.JOB_DESCRIPTION,
              "bg-gray-200": currentStep !== ImportStep.JOB_DESCRIPTION,
            })}
          />
          <div
            className={cn("flex items-center space-x-2", {
              "text-primary [&>div]:bg-primary [&>div]:text-white":
                currentStep === ImportStep.JOB_DESCRIPTION,
              "[&>div]:bg-gray-200 [&>div]:text-gray-400 [&>span]:text-gray-400":
                currentStep !== ImportStep.JOB_DESCRIPTION,
            })}
          >
            <div className="flex h-8 w-8 items-center justify-center rounded-full font-semibold">
              2
            </div>
            <span className="font-semibold">{t`Job Description`}</span>
          </div>
        </div>

        {currentStep === ImportStep.IMPORT_RESUME && (
          <StepUploadOrSelect
            validationResult={validationResult}
            setValidationResult={setValidationResult}
            setCurrentStep={setCurrentStep}
            isValidating={isValidating}
            setIsValidating={setIsValidating}
            onFileUploaded={handleFileUploaded}
            onResumeSelected={handleResumeSelected}
          />
        )}
        {currentStep === ImportStep.JOB_DESCRIPTION && (uploadedFile || selectedResume) && (
          <StepJobDescription
            uploadedFile={uploadedFile}
            uploadedType={uploadedType}
            selectedResume={selectedResume}
            validationResult={validationResult}
            setValidationResult={setValidationResult}
            isValidating={isValidating}
            setIsValidating={setIsValidating}
            setCurrentStep={setCurrentStep}
          />
        )}
      </DialogContent>
    </Dialog>
  );
};
